import { Grid } from "@mui/material";

import { ToggleButton } from "@/Components/v2/atoms/Button";
import { Divider } from "@/Components/v2/atoms/Divider";
import { MetricCardProps } from "@/Components/v2/atoms/MetricCard/MetricCard";
import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import { MetricCards } from "@/Components/v2/molecule/MetricCards";
import { HIDE_DECIMAL, NO_SYMBOL, useConfig } from "@/hooks/useConfig";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { formatPercentage } from "@/pages/scenario-builder/utils";
import { Layer } from "@/Utils/hierarchy";

import { ROIViewToggle } from "../store/useFinancialStore";

interface ROIData {
  rois: {
    total_spend: number;
    total_revenue: number;
    roi: number;
    total_sales: number;
    brand: {
      revenue: number;
      spend: number;
      roi: number;
    };
    category: {
      spend: number;
      revenue: number;
      roi: number;
    };
    product: {
      spend: number;
      revenue: number;
      roi: number;
    };
  };
  brandBreakdown?: Array<{
    brand: string;
    total_spend: number;
    total_revenue: number;
    roi: number;
  }>;
}

interface ROIBreakdownProps {
  data: ROIData;
  formatCurrency: FormatCurrencyFunction;
  roiViewToggle: ROIViewToggle;
  onToggleChange: (toggle: ROIViewToggle) => void;
}

const ROIBreakdown: React.FC<ROIBreakdownProps> = ({
  data,
  formatCurrency,
  roiViewToggle,
  onToggleChange,
}) => {
  const {
    config,
    config: { showReturnInsteadOfRevenue, revenueLabel },
  } = useConfig();
  const { rois, brandBreakdown } = data;

  const revenueFormatterOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
    : HIDE_DECIMAL;

  // Top 4 cards matching the screenshot
  const summaryMetrics: MetricCardProps[] = [
    {
      label: `Total ${revenueLabel}`,
      value: formatCurrency(rois.total_sales, revenueFormatterOptions),
    },
    {
      label: "Aggregate Marketing Spend",
      value: formatCurrency(rois.total_spend, HIDE_DECIMAL),
    },
    {
      label: `Aggregate Marketing ${revenueLabel}`,
      value: formatCurrency(rois.total_revenue, revenueFormatterOptions),
    },
    {
      label: "Aggregate Marketing ROI",
      value: formatPercentage(rois.roi, false),
    },
  ];

  const returnToggleOptionLabel = showReturnInsteadOfRevenue
    ? "Return"
    : `Return (${config.currency.getSymbol()})`;
  const toggleOptions = [
    { label: "Return (%)", value: ROIViewToggle.SUMMARY },
    { label: returnToggleOptionLabel, value: ROIViewToggle.BREAKDOWN },
  ];

  return (
    <Stack spacing={3}>
      {/* Top 4 Cards */}
      <MetricCards metrics={summaryMetrics} />

      {/* Sales Breakdowns Section */}
      <RoundedBorderContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight={600} fontSize={24}>
            Sales Breakdowns
          </Typography>
          <ToggleButtonGroup exclusive value={roiViewToggle} size="small" sx={{ width: 200 }}>
            {toggleOptions.map((option) => (
              <ToggleButton
                key={option.value}
                value={option.value}
                onClick={() => onToggleChange(option.value)}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Divider sx={{ my: 3 }} />

        {/* Sales Breakdown by Marketing Type */}
        <Stack gap={5}>
          <Stack gap={1.5}>
            <Typography variant="h6" fontWeight={600} fontSize={16}>
              Sales Breakdown by Marketing Type
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={3}>
                <Stack>
                  <Typography variant="body2" color="textSecondary">
                    {config.getLayerLabel(Layer.Brand)} Marketing
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {roiViewToggle === ROIViewToggle.SUMMARY
                      ? formatPercentage(rois.brand.roi, false)
                      : formatCurrency(rois.brand.revenue, revenueFormatterOptions)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stack>
                  <Typography variant="body2" color="textSecondary">
                    {config.getLayerLabel(Layer.Category)} Marketing
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {roiViewToggle === ROIViewToggle.SUMMARY
                      ? formatPercentage(rois.category.roi, false)
                      : formatCurrency(rois.category.revenue, revenueFormatterOptions)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stack>
                  <Typography variant="body2" color="textSecondary">
                    {config.getLayerLabel(Layer.Product)} Marketing
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {roiViewToggle === ROIViewToggle.SUMMARY
                      ? formatPercentage(rois.product.roi, false)
                      : formatCurrency(rois.product.revenue, revenueFormatterOptions)}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>

          {/* Sales Breakdown by Brand */}
          {brandBreakdown && brandBreakdown.length > 0 ? (
            <Stack gap={1.5}>
              <Typography variant="h6" fontWeight={600} fontSize={16}>
                Sales Breakdown by Brand
              </Typography>
              <Grid container spacing={2}>
                {brandBreakdown
                  .sort((a, b) => a.brand.localeCompare(b.brand))
                  .map((brand, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Stack>
                        <Typography variant="body2" color="textSecondary">
                          {brand.brand}
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {roiViewToggle === ROIViewToggle.SUMMARY
                            ? formatPercentage(brand.roi, false)
                            : formatCurrency(brand.total_revenue, revenueFormatterOptions)}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
              </Grid>
            </Stack>
          ) : null}
        </Stack>
      </RoundedBorderContainer>
    </Stack>
  );
};

export default ROIBreakdown;
